import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Dices, Video, Mic, SkipForward } from "lucide-react";
import { CoinIcon } from "@/components/Icons";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useCall } from "@/context/CallContext";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

export default function Discover() {
  const [mode, setMode] = useState("video");
  const [match, setMatch] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const excludeRef = useRef([]);
  const { requestCall } = useCall();
  const navigate = useNavigate();

  const roll = async () => {
    setSpinning(true);
    setMatch(null);
    try {
      const [{ data }] = await Promise.all([
        api.get(`/calls/discover/match?mode=${mode}&exclude=${excludeRef.current.join(",")}`),
        new Promise((r) => setTimeout(r, 1300)),
      ]);
      setMatch(data);
      excludeRef.current = [...excludeRef.current.slice(-4), data.match.id];
    } catch (e) {
      toast.error(errText(e));
    }
    setSpinning(false);
  };

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-28 flex flex-col" data-testid="discover-page">
      <button data-testid="discover-back" onClick={() => navigate("/")} className="text-zinc-400 mb-5 flex items-center gap-1 text-sm w-fit"><ArrowLeft size={16} /> Home</button>
      <h1 className="text-2xl font-extrabold font-display">Discover 🎲</h1>
      <p className="text-zinc-500 text-xs mt-1 mb-5">Get matched with a random creator for a 1:1 {mode} call</p>

      <div className="flex bg-[#131A2A] rounded-full p-1 mb-8 w-fit">
        {[["video", Video, "Video"], ["audio", Mic, "Audio"]].map(([k, Icon, label]) => (
          <button key={k} data-testid={`discover-mode-${k}`} onClick={() => { setMode(k); setMatch(null); }}
            className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-bold transition-colors ${mode === k ? "gradient-primary text-white" : "text-zinc-500"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center min-h-[320px]">
        <AnimatePresence mode="wait">
          {spinning && (
            <motion.div key="spin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="discover-spinning">
              <div className="relative w-32 h-32 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full border-2 border-pink-500/50 animate-ping" />
                <span className="absolute inset-3 rounded-full border-2 border-violet-500/50 animate-ping" style={{ animationDelay: "0.3s" }} />
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: "linear" }}>
                  <Dices size={44} className="text-pink-400" />
                </motion.div>
              </div>
              <p className="text-zinc-400 text-xs font-semibold mt-6">Finding your match…</p>
            </motion.div>
          )}

          {!spinning && !match && (
            <motion.button key="idle" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              data-testid="discover-roll-button" onClick={roll}
              className="gradient-primary rounded-full w-36 h-36 flex flex-col items-center justify-center gap-2 text-white font-extrabold shadow-[0_0_40px_rgba(236,72,153,0.5)] active:scale-95 transition-transform">
              <Dices size={38} />
              <span className="text-sm font-display">Match me!</span>
            </motion.button>
          )}

          {!spinning && match && (
            <motion.div key={match.match.id} initial={{ opacity: 0, y: 24, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
              className="glass rounded-3xl p-6 w-full flex flex-col items-center text-center" data-testid="discover-match-card">
              <div className="relative">
                <Avatar src={match.match.avatar} size={88} tier={match.match.tier} level={match.match.level} />
                {match.is_live && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-rose-600 text-white text-[8px] font-extrabold px-2 py-0.5 rounded-full pulse-live">LIVE</span>
                )}
              </div>
              <h2 className="text-white font-extrabold text-lg mt-4 font-display" data-testid="discover-match-name">{match.match.name}</h2>
              <p className="text-zinc-500 text-[10px] mt-0.5">Lv.{match.match.level} · {match.match.tier}</p>
              <p className="text-amber-400 text-xs font-bold mt-3 flex items-center gap-1"><CoinIcon size={14} /> {match.rate}/min {mode} call</p>
              <div className="flex gap-3 mt-6 w-full">
                <button data-testid="discover-skip-button" onClick={roll}
                  className="flex-1 bg-white/10 border border-white/10 text-zinc-200 text-xs font-bold py-3 rounded-full flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <SkipForward size={14} /> Skip
                </button>
                <button data-testid="discover-call-button" onClick={() => requestCall(match.match, mode)}
                  className="flex-[1.4] gradient-primary text-white text-xs font-extrabold py-3 rounded-full flex items-center justify-center gap-1.5 shadow-[0_0_18px_rgba(236,72,153,0.5)] active:scale-95 transition-transform">
                  {mode === "video" ? <Video size={14} /> : <Mic size={14} />} Call now
                </button>
              </div>
              {match.is_live && (
                <button data-testid="discover-watch-live" onClick={() => navigate(`/stream/${match.live_stream_id}`)}
                  className="mt-3 text-[10px] text-rose-400 font-bold">
                  📺 Or watch their live stream →
                </button>
              )}
              <button data-testid="discover-view-profile" onClick={() => navigate(`/profile/${match.match.id}`)} className="mt-2 text-[10px] text-zinc-500 font-semibold">
                View profile
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <BottomNav />
    </div>
  );
}
